import { useEffect, useState } from 'react'

import { Box, Container } from '@mui/material'
import { GAME_STEP } from 'constants/enums/GameStepEnum'
import { GAME_TYPE } from 'constants/enums/GameTypeEnum'
import { ToastSeverity } from 'constants/enums/ToastSeverityEnum'
import { CustomerJourneyTopicType } from 'constants/enums/TopicTypeEnum'
import { SUBMITTED_IDEAS } from 'constants/routes'
import { alertTitleTextMap, defaultErrorMessage } from 'content/alertText'
import { brainstormText } from 'content/brainstormText'
import { hasUnsavedIdeation } from 'utils/ideationUtils'
import logger from 'utils/logger'
import {
	guardUnsavedAction,
	useUnsavedIdeationWarning,
} from 'utils/useUnsavedIdeationWarning'

import { Conversation, useSimonSaysModel } from 'models/SimonSaysModel'
import { Game } from 'models/gameModels'
import {
	SimonSaysPersonaResponse,
	SimonSaysPromptResponse,
} from 'models/ideaModels'

import { useIdeaStreakContext } from 'contexts/IdeaStreakProvider'
import { useModalContext } from 'contexts/ModalProvider'
import { useToastContext } from 'contexts/ToastProvider'

import {
	postProcessImage,
	postSimonSaysResponse,
	updateSimonSaysPersonaIdeaExamples,
} from 'services/completions.service'
import { postIdea } from 'services/ideas.service'

import BreadCrumbs from 'components/BreadCrumbs'
import CustomGameTitleBar from 'components/CustomGameTitleBar/CustomGameTitleBar'
import Hero from 'components/Hero/Hero'
import { IdeationRecommendationModal } from 'components/IdeationRecommendationModal/IdeationRecommendationModal'

import Section1 from './components/Section1/Section1'
import Section2 from './components/Section2'
import Section3 from './components/Section3'

export const TEST_ID = 'simon-says'

export const SimonSaysStep = {
	Step1: 0,
	Step2: 1,
} as const
export type SimonSaysStepType =
	(typeof SimonSaysStep)[keyof typeof SimonSaysStep]

let aiResponse = {
	content: '',
	role: 'assistant',
}
const SimonSays = ({ game }: { game?: Game }) => {
	const [refreshClicksSinceIdeation, setRefreshClicksSinceIdeation] =
		useState(0)
	const [simonSaysPrompt, setSimonSaysPrompt] =
		useState<SimonSaysPromptResponse>()

	const {
		customer,
		randomElement,
		innovationCompany,
		setInnovationCompany,
		promptConversation,
		setPromptConversation,
		customerJourney: companyProblem,
		setCustomerJourney: setCompanyProblem,
		setCustomerExperienceDisclaimerChecked,
		customerExperienceDisclaimerChecked,
		authorIdeation,
		setAuthorIdeation,
		innovationTopic,
		setPromptResponse,
		responseConversation,
		setResponseConversation,
		currentResponses,
		setCurrentResponses,
		promptToJSON,
		responseToJSON,
		imageToJSON,
		resetSoft,
		resetImage,
		gameToJSON,
		reset,
		generateImageDisabled,
		generateScenarioDisabled,
		imageData,
		setImageData,
		imageAvailable,
		setImageAvailable,
		submitIdeasDisabled,
		setCustomerValues,
		setGameId,
		termsAgreed,
		setTermsAgreed,
	} = useSimonSaysModel()

	useEffect(() => {
		if (game) {
			setGameId(game.gameId)
			setInnovationCompany(game.innovationCompany)
			setCompanyProblem(game.companyProblem)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [game])

	const { showModal } = useModalContext()
	const [isPromptLoading, setIsPromptLoading] = useState(false)
	const [isSubmitButtonLoading, setIsSubmitButtonLoading] = useState(false)
	const [, setIsAiLoading] = useState(false)
	const [step, setStep] = useState<GAME_STEP>(GAME_STEP.Step1)
	const [previousPrompts, setPreviousPrompts] = useState<string[]>([])
	const hasUnsavedIdeas = hasUnsavedIdeation(step, authorIdeation)

	useUnsavedIdeationWarning(hasUnsavedIdeas, authorIdeation, showModal)

	const [isCurrentResponseLoading, setIsCurrentResponseLoading] =
		useState(false)
	const [isAiImageLoading, setIsAiImageLoading] = useState(false)
	const { setIdeaStreakCount } = useIdeaStreakContext()
	const { showAlert } = useToastContext()

	const userMessage = {
		content: '',
		role: 'user',
	}

	const updateAIResponse = (content: string) => {
		return {
			content,
			role: 'assistant',
		}
	}

	const resetAllField = () => {
		reset()
	}

	const handleAiResponseClick =
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		async (_additionalProps = {}) => {
			// prevent calling before values are set
			if (!randomElement.length && !!customer) {
				return
			}

			setIsAiLoading(true)
			setIsCurrentResponseLoading(true)
			setCurrentResponses([])

			try {
				// setup the conversation
				const prompt = responseToJSON()
				prompt.conversation = [...responseConversation, userMessage]

				const response = await postSimonSaysResponse({
					customer,
					innovationCompany,
					problemDescription: companyProblem,
					randomElement,
					previousPrompts: previousPrompts.join('\n'),
					conversation: prompt.conversation,
				})

				if (response.status === 200 && response.data) {
					let msg: string = ''
					if (
						response.data.responses &&
						Array.isArray(response.data.responses)
					) {
						msg = JSON.stringify(response.data)
					}

					if (msg) {
						// add response to state
						setResponseConversation((prevMessages) => [
							...prevMessages,
							userMessage,
							updateAIResponse(msg),
						])

						setCurrentResponses(response.data.responses)
						setIsCurrentResponseLoading(false)
						setPromptResponse(response.data)
					} else {
						if (showAlert) {
							showAlert(defaultErrorMessage)
						}
					}
				} else {
					throw Error(`response received ${response}`)
				}
			} catch (error) {
				logger.error('Failed to get AI response:', error)
				if (showAlert) {
					showAlert({
						title: alertTitleTextMap.genAiError.title,
						bodyText: alertTitleTextMap.genAiError.text,
						severity: ToastSeverity.ERROR,
					})
				}
			} finally {
				setIsAiLoading(false)
				setIsCurrentResponseLoading(false)
			}
		}

	const generateAiImage = async () => {
		logger.debug('generateAiImage running')
		resetImage()
		setIsAiImageLoading(true)

		try {
			logger.debug('calling postProcessImage')

			const response = await postProcessImage(imageToJSON())

			setImageData(response.data)
			setImageAvailable(true)

			// Show success notification for image generation
			if (showAlert) {
				showAlert({
					title: alertTitleTextMap.imageGenerated.title,
					bodyText: alertTitleTextMap.imageGenerated.text,
					severity: ToastSeverity.SUCCESS,
				})
			}
		} catch (error) {
			logger.error('Failed to generate image:', error)
			if (showAlert) {
				showAlert({
					title: alertTitleTextMap.imageGenerationError.title,
					bodyText: alertTitleTextMap.imageGenerationError.text,
					severity: ToastSeverity.ERROR,
				})
			}
		} finally {
			setIsAiImageLoading(false)
		}
	}

	const viewSubmittedIdeas = () => {
		window.open(`${window.location.origin}${SUBMITTED_IDEAS}`, '_blank')
	}

	const refreshPrompt = async () => {
		return guardUnsavedAction({
			isUnsaved: hasUnsavedIdeas,
			showModal,
			onProceed: async () => {
				if (refreshClicksSinceIdeation < 2) {
					await createPrompt()
				}

				setRefreshClicksSinceIdeation((prev) => prev + 1)
			},
		})
	}

	const createPrompt = async () => {
		try {
			setIsPromptLoading(true)
			resetSoft()

			const prompt = promptToJSON()
			prompt.conversation = [...promptConversation, userMessage]
			setStep(GAME_STEP.Step2)
			window.scrollTo(0, 0)
			const previousPromptsString = previousPrompts.join('\n\n')
			const response = await postSimonSaysResponse({
				customer,
				innovationCompany,
				problemDescription: companyProblem,
				previousPrompts: previousPromptsString,
				randomElement,
				conversation: prompt.conversation,
			})

			if (response.status === 200 && response.data) {
				if (typeof response.data !== 'object') {
					throw Error(
						`invalid response type. expected: object, actual: ${typeof response.data}`
					)
				}

				setSimonSaysPrompt(response)

				setPreviousPrompts((prev) => [
					...prev,
					response?.data?.personas
						?.map((persona: SimonSaysPersonaResponse) => {
							return `${persona.role}: ${persona.overview}`
						})
						.join('\n') ?? '',
				])
			}
		} catch (error) {
			logger.error(error)
			if (showAlert) {
				showAlert({
					title: alertTitleTextMap.scenarioGenerationError.title,
					bodyText: alertTitleTextMap.scenarioGenerationError.text,
					severity: ToastSeverity.ERROR,
				})
			}
		} finally {
			setIsPromptLoading(false)
		}
	}

	const handleSubmitIdeas = async () => {
		setIsSubmitButtonLoading(true)

		try {
			const response = await postIdea(gameToJSON())

			if (showAlert) {
				if (response.status === 200) {
					showAlert({
						severity: ToastSeverity.SUCCESS,
					})
					setIdeaStreakCount((prevState) => prevState + 1)
					createPrompt()
				} else {
					throw new Error('Failed to submit idea')
				}
			}
		} catch (error) {
			logger.error(error)
			if (showAlert) {
				showAlert({
					title: alertTitleTextMap.submitIdeaError.title,
					bodyText: alertTitleTextMap.submitIdeaError.text,
					severity: ToastSeverity.ERROR,
				})
			}
		} finally {
			setIsSubmitButtonLoading(false)
		}
	}

	const handleManualPromptChange = (customer: string) => {
		setCustomerValues(customer)
		const msg = JSON.stringify({
			customer,
			randomElement,
			resetPreviousElements: false,
		})

		aiResponse = {
			content: msg,
			role: 'user',
		}

		setPromptConversation((prevMessages: Conversation[]) => [
			...prevMessages,
			userMessage,
			aiResponse,
		])
	}

	const onIdeationRecommendationBackClicked = () => {
		setStep(GAME_STEP.Step1)
		onIdeationRecommendationModalClosed()
	}

	const onIdeationRecommendationModalClosed = () => {
		setRefreshClicksSinceIdeation(0)
	}

	const handleRegenerateExampleIdeas = async (
		personas: SimonSaysPersonaResponse[],
		personaIndex: number
	) => {
		return guardUnsavedAction({
			isUnsaved: hasUnsavedIdeas,
			showModal,
			onProceed: async () => {
				try {
					setIsCurrentResponseLoading(true)
					const persona = personas.at(personaIndex)

					if (!persona) {
						throw new Error('Persona is undefined')
					}

					const response = await updateSimonSaysPersonaIdeaExamples({
						persona,
						companyName: innovationCompany,
						problemDescription: companyProblem,
					})

					if (response.status === 200 && response.data.exampleIdeas) {
						const updatedPersonas = {
							...response,
							data: {
								personas: personas.map((persona, index) => {
									return index === personaIndex
										? {
												...persona,
												exampleIdeas:
													response.data.exampleIdeas,
											}
										: persona
								}),
							},
						}

						setSimonSaysPrompt(updatedPersonas)

						if (showAlert) {
							showAlert({
								title: alertTitleTextMap.exampleIdeasRegenerated
									.title,
								bodyText:
									alertTitleTextMap.exampleIdeasRegenerated
										.text,
								severity: ToastSeverity.SUCCESS,
							})
						}
					}
				} catch (error) {
					logger.error('Failed to regenerate example ideas:', error)
					if (showAlert) {
						showAlert({
							title: alertTitleTextMap
								.exampleIdeasRegenerationError.title,
							bodyText:
								alertTitleTextMap.exampleIdeasRegenerationError
									.text,
							severity: ToastSeverity.ERROR,
						})
					}
					throw error
				} finally {
					setIsCurrentResponseLoading(false)
				}
			},
		})
	}

	useEffect(() => {
		setRefreshClicksSinceIdeation(0)
	}, [authorIdeation])

	useEffect(() => {
		if (randomElement.length && !!customer) {
			handleAiResponseClick()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [randomElement, customer])

	return (
		<Box>
			<IdeationRecommendationModal
				refreshClicksSinceIdeation={refreshClicksSinceIdeation}
				onBack={onIdeationRecommendationBackClicked}
				onClose={onIdeationRecommendationModalClosed}
			/>
			<Hero
				step={step}
				gameTypeEnum={GAME_TYPE.SIMON_SAYS}
				innovationTopic={innovationTopic as CustomerJourneyTopicType}
				innovationCompany={innovationCompany}
				companyProblem={companyProblem}
				isLoading={isPromptLoading}
				setCompanyProblem={setCompanyProblem}
				setInnovationCompany={setInnovationCompany}
				createPrompt={createPrompt}
				hideEdit={Boolean(game && (game.gameTitle || game.endTime))}
			/>
			<Container
				data-testid={`${TEST_ID}-container`}
				maxWidth={step === GAME_STEP.Step1 ? 'md' : 'lg'}
				sx={{
					paddingTop: '3rem',
					paddingBottom: '3rem',
					paddingX: '2rem',
				}}
			>
				<Box paddingBottom={'2rem'}>
					<BreadCrumbs
						breadcrumbs={[
							{
								label: '1. INNOVATION TOPIC',
								active: step === SimonSaysStep.Step1,
								onClick:
									step !== SimonSaysStep.Step1
										? () => setStep(SimonSaysStep.Step1)
										: undefined,
							},
							{
								label: '2. FACILITATION TECHNIQUE & 3. IDEATION',
								active: step !== SimonSaysStep.Step1,
							},
						]}
					/>
				</Box>
				{game && (game.gameTitle || game.endTime) && (
					<CustomGameTitleBar game={game} />
				)}
				{step === GAME_STEP.Step1 && (
					<Section1
						isLoading={isPromptLoading}
						innovationCompany={innovationCompany}
						setInnovationCompany={setInnovationCompany}
						companyProblem={companyProblem}
						setCompanyProblem={setCompanyProblem}
						customerExperienceDisclaimerChecked={
							customerExperienceDisclaimerChecked
						}
						setCustomerExperienceDisclaimerChecked={
							setCustomerExperienceDisclaimerChecked
						}
						generateScenarioDisabled={generateScenarioDisabled}
						resetAllField={resetAllField}
						createPrompt={createPrompt}
						editable={
							game
								? game.gameTitle === '' && game.endTime === ''
								: true
						}
					/>
				)}
				{step === GAME_STEP.Step2 && (
					<>
						<Section2
							isAiImageLoading={isAiImageLoading}
							isCurrentResponseLoading={isCurrentResponseLoading}
							imageData={imageData}
							imageAvailable={imageAvailable}
							loading={isPromptLoading}
							customer={customer}
							currentResponses={currentResponses}
							manualChangeCustomerPrompts={
								handleManualPromptChange
							}
							onRegenerateExampleIdeas={
								handleRegenerateExampleIdeas
							}
							randomElement={randomElement}
							simonSaysPrompt={simonSaysPrompt}
							createPrompt={refreshPrompt}
							generateAiImage={generateAiImage}
							generateImageDisabled={generateImageDisabled}
							resetImage={resetImage}
						/>
						<Section3
							loading={isSubmitButtonLoading}
							submitIdeas={handleSubmitIdeas}
							authorIdeation={authorIdeation}
							setAuthorIdeation={setAuthorIdeation}
							viewSubmittedIdeas={viewSubmittedIdeas}
							submitIdeasDisabled={submitIdeasDisabled}
							cautionText={
								game?.gameTitle || game?.endTime
									? brainstormText.global.alternateWarningText
									: brainstormText.global.warningText
							}
							termsAgreed={termsAgreed}
							setTermsAgreed={setTermsAgreed}
							gameId={game?.gameId}
						/>
					</>
				)}
			</Container>
		</Box>
	)
}

export default SimonSays
